import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Breadcrumbs } from '@saucelabs/ds-breadcrumbs';
import { Typography } from '@saucelabs/ds-typography';
import { HStack } from '@saucelabs/sl-component-grid';
import PartyingFace from '@saucelabs/sl-images/lib/emoji/emoji-partying-face.svg';
import { IconAutomatedColor, IconLiveTimeColor } from '@saucelabs/ds-icons';
import { ButtonText } from '@saucelabs/ds-button';

import {
  ROUTE_PARENT_SECTION,
  ROUTE_SECTION_INVOICES,
} from '../constants';
import { formatThousand } from '../utils/numbers';
import SideNavigation from '../components/SideNavigation';
import { trackEvent, EVENTS, MODE_USAGE } from '../tracking';
import Styles from './CheckoutSuccess.styles';
import { BackToPlatformSpan } from '../components/BackToPlatformLink';

const LINK_LIVE_APP_TESTING = '/live/app-testing';
const LINK_LIVE_DESKTOP = '/live/web-testing/desktop';
const LINK_LIVE_REAL = '/live/web-testing/device';
const LINK_LIVE_VIRTUAL = '/live/web-testing/virtual';
const LINK_PLATFORM_CONFIGURATOR = '/platform-configurator';
const LINK_TUNNELS = '/tunnels';

const crumbList = [
  { name: 'Subscription and Billing', url: `/${ROUTE_PARENT_SECTION}` },
  { name: 'Success' },
];

const CheckoutSuccessContainer = () => {
  const location = useLocation();
  const email = location?.state?.email || 'your email';
  const credits = location?.state?.credits ? formatThousand(location.state.credits) : 'some';

  useEffect(() => { trackEvent(EVENTS.CHECKOUT_SUCCESS_PAGE, MODE_USAGE); }, []);

  return (
    <div
      className="bg-white min-h-full text-gray-900"
      data-qa="purchase-success-view"
    >
      <Breadcrumbs list={crumbList}>
        <SideNavigation />
      </Breadcrumbs>
      <div
        className="max-w-4xl mx-auto"
      >
        <div className="my-0 text-center">
          <PartyingFace className="w-12 h-12 mt-5 mb-1" />
        </div>
        <Typography
          className="mt-0 mb-1 text-center"
          disableSpacing
          variant="heading-1"
        >
          {`Yay! You successfully purchased ${credits} credits!`}
        </Typography>
        <Typography
          className="mb-4 text-center font-semibold"
          component="p"
          variant="body-large"
        >
          {`An invoice has been sent to ${email}, you can also `}
          <Link to={`/${ROUTE_PARENT_SECTION}/${ROUTE_SECTION_INVOICES}`}>
            view the invoice
          </Link>
          {' in your invoices page.'}
        </Typography>
      </div>
      <div
        className={`mx-auto ${Styles['quick-links']}`}
      >
        <Typography
          className="text-center mb-2"
          disableSpacing
          variant="heading-3"
        >
          Quick links to help you test:
        </Typography>
        <HStack
          alignItems="stretch"
          gap={2}
          justifyContent="space-between"
        >
          <div className="w-full p-2 border border-solid rounded border-gray-300 font-semibold opacity-90">
            <HStack
              alignItems="center"
            >
              <IconLiveTimeColor className="mr-1.5" />
              <Typography variant="body-large">
                Manual Testing
              </Typography>
            </HStack>
            <ul className="list-none list-outside m-0 p-0 mt-2 text-base">
              <li className="mb-1">
                <Link to={LINK_LIVE_APP_TESTING}>
                  Mobile app
                </Link>
              </li>
              <li className="mb-1">
                <Link to={LINK_LIVE_VIRTUAL}>
                  {'Mobile browser '}
                  <span className="font-light">
                    (virtual device)
                  </span>
                </Link>
              </li>
              <li className="mb-1">
                <Link to={LINK_LIVE_REAL}>
                  {'Mobile browser '}
                  <span className="font-light">
                    (real device)
                  </span>
                </Link>
              </li>
              <li className="mb-1">
                <Link to={LINK_LIVE_DESKTOP}>
                  Desktop browser
                </Link>
              </li>
            </ul>
          </div>
          <div className="w-full p-2 border border-solid rounded border-gray-300 font-semibold opacity-90">
            <HStack alignItems="center">
              <IconAutomatedColor className="mr-1.5" />
              <Typography variant="body-large">
                Automated Testing
              </Typography>
            </HStack>
            <ul className="list-none list-outside m-0 p-0 mt-2 text-base">
              <li className="mb-1">
                <Link to={LINK_LIVE_APP_TESTING}>
                  Upload your iOS app
                </Link>
              </li>
              <li className="mb-1">
                <Link to={LINK_LIVE_APP_TESTING}>
                  Upload your Android app
                </Link>
              </li>
              <li className="mb-1">
                <Link to={LINK_PLATFORM_CONFIGURATOR}>
                  Platform configurator
                </Link>
              </li>
              <li className="mb-1">
                <Link to={LINK_TUNNELS}>
                  Test with a Tunnel
                </Link>
              </li>
            </ul>
          </div>
        </HStack>
        <div className="text-center mt-4">
          <ButtonText
            to="/"
            variant="outline"
          >
            <BackToPlatformSpan />
          </ButtonText>

        </div>
      </div>
    </div>
  );
};

export { CheckoutSuccessContainer };
export default CheckoutSuccessContainer;
